import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import LogoUpload from "../../components/common/logoUpload";
import dashboardService from "../../services/DashboardServices";
import { Spaces, SpeacesFormErrors } from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";

interface SpacesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  spaceUpdateData?: Spaces;
}

const SpacesModal: React.FC<SpacesModalProps> = ({
  open,
  onClose,
  onSave,
  spaceUpdateData,
}) => {
  const [formData, setFormData] = useState<Spaces>({
    id: null,
    title: "",
    logoUrl: "",
    logo: null,
    spaceLink: "",
  });
  const [formErrors, setFormErrors] = useState({
    title: "",
    spaceLink: "",
    logoUrl: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if (spaceUpdateData) {
      setFormData({
        id: spaceUpdateData.id || null,
        title: spaceUpdateData.title || "",
        logoUrl: spaceUpdateData.logoUrl || "",
        logo: null,
        spaceLink: spaceUpdateData.spaceLink || "",
      });
    }
  }, [spaceUpdateData]);

  const handleCloseSpaceModal = () => {
    setFormData({
      id: null,
      title: "",
      logoUrl: "",
      logo: null,
      spaceLink: "",
    });

    setFormErrors({
      title: "",
      spaceLink: "",
      logoUrl: "",
    });

    onClose();
  };

  //// input onchange code
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  /// handle file upload logo
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, logo: file }));
    if (file) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        logoUrl: "",
      }));
    }
  };
  /// handle Form validation code
  const validateForm = () => {
    const errors: SpeacesFormErrors = {};
    const urlRegex =
      /^(https?:\/\/)(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(\/[^\s]*)?$/i;

    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }

    if (!formData.spaceLink.trim()) {
      errors.spaceLink = "Google Space link is required.";
    } else if (!urlRegex.test(formData.spaceLink.trim())) {
      errors.spaceLink = "Enter a valid URL for the Google Space link.";
    }

    const logoUrl = formData.logoUrl?.trim() || "";
    if (!logoUrl && !formData.logo) {
      errors.logoUrl = "Either a file or a URL must be provided.";
      showToast(errors.logoUrl, "error");
    } else if (logoUrl && !urlRegex.test(logoUrl)) {
      errors.logoUrl = "Enter a valid URL for the logo.";
    }

    setFormErrors({
      title: errors.title || "",
      spaceLink: errors.spaceLink || "",
      logoUrl: errors.logoUrl || "",
    });

    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;
    try {
      setBtnLoading(true);
      const { id, title, spaceLink, logoUrl, logo } = formData;

      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() || "0");
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("SpaceLink", spaceLink.trim());

      if (logo instanceof File) {
        formDataToSend.append("Logo", logo);
        formDataToSend.append("LogoUrl", "");
      } else if (logoUrl?.trim()) {
        formDataToSend.append("LogoUrl", logoUrl.trim());
        formDataToSend.append("Logo", "");
      }

      await dashboardService
        .addUpdateSpaceDataAPI(formDataToSend)
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            onSave();
            onClose();
          } else {
            showToast(response?.data?.message, "error");
          }
        })
        .catch((error) => {
          console.error("Error", error);
        })
        .finally(() => {
          setBtnLoading(false);
        });
    } catch (error) {
      console.error("Error saving space data:", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleCloseSpaceModal}
      title="Spaces"
      onSave={handleSaveData}
      btnLoading={btnLoading}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              error={formErrors.title}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LogoUpload
              label="Upload Logo"
              onChange={handleFileUpload}
              fileName={formData.logo instanceof File ? formData.logo.name : ""}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <CustomInput
              label="Upload logo via URL"
              type="text"
              name="logoUrl"
              placeholder="Enter URL"
              value={formData.logoUrl || ""}
              onChange={handleChange}
              error={formErrors.logoUrl}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="Google Space Link"
              type="text"
              name="spaceLink"
              placeholder="Enter the Google Space Link URL"
              value={formData.spaceLink}
              onChange={handleChange}
              error={formErrors.spaceLink}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default SpacesModal;
